import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppIcon from "../../../components/ui/AppIcon";
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";

export default function LeftSidebar({
    onSearchOpen,
    onCreateChannel,
}) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { channels, directMessages, activeChat, unreadCounts, draftsStatus, userDrafts, setShowDraftsModal, onlineUsers } = useChat();
    const [orgName, setOrgName] = useState("Workspace");

    const role = user?.user_role?.role || user?.role || '';
    const isPrivileged = role === 'admin' || role === 'superadmin';

    useEffect(() => {
        const storedOrg = localStorage.getItem("org_name");
        if (storedOrg) setOrgName(storedOrg);
    }, []);

    const workspace = {
        id: "default",
        name: orgName,
        color: "#6C63FF",
        initial: orgName.charAt(0).toUpperCase()
    };

    return (
        <aside className="h-full flex overflow-hidden">

            {/* MAIN SIDEBAR: Channel & DM List */}
            <div className="w-[260px] flex flex-col bg-app-sidebar border-r border-border-subtle">

                {/* Workspace Header */}
                {/* <header className="px-4 py-4 flex items-center justify-between group cursor-pointer hover:bg-black/5 transition-colors">
                    <div className="min-w-0">
                        <h1 className="text-[15px] font-bold text-text-primary tracking-tight flex items-center gap-1.5 truncate">
                            {workspace.name}
                            <AppIcon name="ChevronDownIcon" size={12} className="opacity-40 flex-shrink-0" />
                        </h1>
                        <p className="text-[10px] text-text-muted font-bold uppercase tracking-widest">Enterprise</p>
                    </div>
                </header> */}

                {/* Search Bar */}
                <div className="px-3 mb-4 mt-4">
                    <div
                        onClick={onSearchOpen}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-app-bg border border-border-subtle hover:border-border-strong text-text-muted cursor-pointer transition-all shadow-subtle"
                    >
                        <AppIcon name="MagnifyingGlassIcon" size={14} />
                        <span className="text-[13px] font-medium">Search conversations</span>
                        <span className="ml-auto text-[10px] opacity-40 border border-border-subtle px-1 rounded uppercase">⌘K</span>
                    </div>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 overflow-y-auto px-2 space-y-4 custom-scrollbar">

                    {/* Top Level Nav */}
                    <div className="space-y-0.5">
                        {/* <NavItem icon="ChatBubbleLeftRightIcon" label="All messages" /> */}
                        {/* <NavItem icon="BookmarkIcon" label="Saved items" /> */}
                        <NavItem
                            icon="PencilSquareIcon"
                            label="Drafts"
                            badge={userDrafts.length}
                            onClick={() => setShowDraftsModal(true)}
                        />
                    </div>

                    {/* Channels Group */}
                    <div>
                        <div className="px-3 py-1 flex items-center justify-between group cursor-pointer mb-1">
                            <span className="text-[10px] font-bold text-text-muted tracking-wider uppercase">Channels</span>
                            {isPrivileged && (
                                <button onClick={() => { if (!isPrivileged) return; onCreateChannel?.(); }} className="p-1 rounded hover:bg-bg-muted opacity-0 group-hover:opacity-100 transition-opacity">
                                    <AppIcon name="PlusIcon" size={14} className="text-text-muted" />
                                </button>
                            )}
                        </div>
                        <div className="space-y-0.5">
                            {channels.map(ch => (
                                <ChannelItem
                                    key={ch._id || ch.id}
                                    id={ch._id || ch.id}
                                    label={ch.name}
                                    type={ch.isPrivate ? 'private' : 'public'}
                                    unread={unreadCounts[ch._id || ch.id] || 0}
                                    hasDraft={!!draftsStatus[ch._id || ch.id]}
                                    active={activeChat?.type === 'channel' && (activeChat?.data?._id === ch._id || activeChat?.data?.id === ch.id)}
                                    onClick={() => navigate(`/channel/${ch._id || ch.id}`)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* DMs Group */}
                    <div>
                        <div className="px-3 py-1 flex items-center justify-between mb-1">
                            <span className="text-[10px] font-bold text-text-muted tracking-wider uppercase">Direct Messages</span>
                        </div>
                        <div className="space-y-0.5">
                            {directMessages.filter(dm => (dm._id || dm.id) !== user?.id).map(dm => (
                                <DMItem
                                    key={dm._id || dm.id}
                                    id={dm._id || dm.id}
                                    label={dm.name || dm.displayName}
                                    avatar={dm.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(dm.name || dm.displayName || 'User')}&background=random`}
                                    status={onlineUsers.includes(dm._id || dm.id) ? "online" : "offline"}
                                    unread={unreadCounts[dm._id || dm.id] || 0}
                                    hasDraft={!!draftsStatus[dm._id || dm.id]}
                                    active={activeChat?.type === 'dm' && (activeChat?.data?._id === dm._id || activeChat?.data?.id === dm.id)}
                                    onClick={() => navigate(`/dm/${dm._id || dm.id}`)}
                                    sidebarBg="bg-app-sidebar"
                                />
                            ))}
                        </div>
                    </div>
                </nav>

                {/* User Profile Footer */}
                <footer className="p-3 bg-black/5 border-t border-border-subtle mt-auto">
                    <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-black/5 cursor-pointer transition-colors group">
                        <div className="relative">
                            <img
                                src={user?.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'Me')}&background=5E6AD2&color=fff`}
                                className="w-9 h-9 rounded-xl object-cover border border-border-subtle shadow-subtle"
                                alt="Me"
                            />
                            <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-app-sidebar rounded-full flex items-center justify-center border border-app-sidebar">
                                <div className="w-2 h-2 bg-online rounded-full shadow-[0_0_8px_rgba(34,197,94,0.4)]" />
                            </div>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <p className="text-[13px] font-bold text-text-primary truncate">{user?.name || 'Me'}</p>
                            <p className="text-[10px] text-online font-bold uppercase tracking-wider">Active</p>
                        </div>
                        <AppIcon name="Cog6ToothIcon" size={18} className="text-text-muted group-hover:rotate-45 transition-transform" />
                    </div>
                </footer>

            </div>
        </aside>
    );
}

/* HELPER COMPONENTS */
function NavItem({ icon, label, badge, badgeColor, onClick }) {
    return (
        <button onClick={onClick} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] font-medium text-text-secondary hover:bg-black/5 hover:text-text-primary transition-all group">
            <AppIcon name={icon} size={18} className="opacity-60 group-hover:opacity-100 transition-opacity" />
            <span>{label}</span>
            {badge > 0 && (
                <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${badgeColor || "bg-primary"} text-white shadow-sm`}>
                    {badge}
                </span>
            )}
        </button>
    );
}

function ChannelItem({ id, label, type, unread, active, hasDraft, onClick }) {
    return (
        <div
            onClick={onClick}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] font-medium transition-all duration-150 cursor-pointer shadow-subtle ${active ? "bg-primary/10 text-primary font-bold shadow-sm" : "text-text-secondary hover:bg-black/5 hover:text-text-primary"} ${hasDraft ? 'border-l-2 border-warning bg-warning/5' : ''}`}
        >
            <span className="opacity-40 font-bold w-4 text-center">{type === 'private' ? '🔒' : '#'}</span>
            <span className={`flex-1 truncate ${unread > 0 ? 'text-text-primary font-bold' : ''}`}>{label}</span>
            {unread > 0 && <span className="min-w-[20px] h-[20px] flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 bg-primary text-white shadow-subtle">{unread}</span>}
        </div>
    );
}

function DMItem({ id, label, avatar, status, unread, hasDraft, active, onClick, sidebarBg }) {
    const statusColors = {
        online: "bg-online shadow-[0_0_8px_rgba(34,197,94,0.4)]",
        away: "bg-away",
        busy: "bg-error",
        offline: "bg-text-muted"
    };

    return (
        <div
            onClick={onClick}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] font-medium transition-all duration-150 cursor-pointer shadow-subtle ${active ? "bg-primary/10 text-primary font-bold shadow-sm" : "text-text-secondary hover:bg-black/5 hover:text-text-primary"} ${hasDraft ? 'border-l-2 border-warning bg-warning/5' : ''}`}
        >
            <div className="relative flex-shrink-0">
                <img src={avatar} className="w-6 h-6 rounded-lg object-cover border border-border-subtle shadow-subtle" alt={label} />
                <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 ${sidebarBg || 'bg-app-sidebar'} rounded-full flex items-center justify-center border border-border-subtle`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${statusColors[status] || "bg-text-muted"}`} />
                </div>
            </div>
            <span className={`flex-1 truncate ${unread > 0 ? 'text-text-primary font-bold' : ''}`}>{label}</span>
            {unread > 0 && <span className="min-w-[20px] h-[20px] flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 bg-primary text-white shadow-subtle">{unread}</span>}
        </div>
    );
}